import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';

import useFilters from '@/hooks/useFilters';
import {merchantService} from '@/services/merchants';
import {tierService} from '@/services/tierService';
import type {AfriexRatePromo, UserType} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';

import type {TierListProps} from './types';

const useTierList = (
  target: UserType,
  businessId?: string,
  // When false, the hook will not fetch. Use this to defer the request until
  // a required dependency (e.g. the businessId) is available, so we never
  // fire an unscoped request that can race a later scoped one and win.
  enabled = true,
): TierListProps<AfriexRatePromo> => {
  const [tierList, setTierList] = useState<AfriexRatePromo[]>([]);
  const [fullTierList, setFullTierList] = useState<AfriexRatePromo[]>([]);
  // Start in the loading state while disabled so consumers show a skeleton
  // (not an empty list) until the fetch is allowed to run.
  const [isLoading, setIsLoading] = useState<boolean>(!enabled);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [serverTotal, setServerTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  // Track the last-fetched page (0-indexed, matching the params) so an action
  // (create/update a promo) can refetch and land the admin back on the same
  // page instead of resetting to page 1.
  const currentPageRef = useRef<number>(0);
  // 1-indexed page synced back to Pagination after a direct/post-action fetch.
  const [displayPage, setDisplayPage] = useState<number>(1);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const rawStatus = queryParams.get('status');
  const status = (
    rawStatus && rawStatus !== 'all' ? rawStatus : null
  ) as AfriexQueryParams['status'];
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);

  const {applyFilters} = useFilters(setCurrentFilters);

  // Layer the `status` URL param onto the filters the request is built from
  // instead of mirroring it into `currentFilters` state via an effect. That
  // effect ran after the first render and, via the unconditional `omit` in
  // its `else` branch, always handed `currentFilters` a fresh object
  // identity, which recreated `fetchAllTierList` and fired a second request
  // on every mount, status or not. `currentFilters` now holds only the
  // filters the admin picked, so its identity is stable and this memo is
  // stable with it.
  const effectiveFilters = useMemo(
    () => (status ? {...currentFilters, status} : currentFilters),
    [currentFilters, status],
  );

  const fetchAllTierList = useCallback(
    async (params: AfriexQueryParams) => {
      // Defer until the scope is ready (e.g. businessId resolved) so we never
      // fetch and momentarily render the unscoped list.
      if (!enabled) {
        return;
      }
      try {
        setError(null);
        setIsLoading(true);
        //use page from params or default to 1
        const requestedPage = params.page || 0;
        const page = requestedPage + 1;
        if (businessId) {
          //the BE returns every promo for this business unpaged, so page here
          const tierData = await merchantService.getBusinessRatePromos({
            limit: 100,
            businessId,
          });
          //set full list and total
          const fullTierList = tierData.data;
          setFullTierList(fullTierList);
          setServerTotal(tierData.total);
          setItemTotal(tierData.total);
          //calculate start and end indices for the requested page
          const startIndex = (page - 1) * 10;
          const endIndex = startIndex + 10;
          //slice fullTierList to get 10 items for the current page
          setTierList(fullTierList.slice(startIndex, endIndex));
        } else {
          const tierData = await tierService.getTierList({
            page: requestedPage,
            limit: params.limit ?? 10,
            target,
          });
          setFullTierList(tierData.data);
          setTierList(tierData.data);
          setServerTotal(tierData.total);
          setItemTotal(tierData.total);
        }
        //remember the requested page so a post-action refetch stays on it
        currentPageRef.current = requestedPage;
        setDisplayPage(page);
        setPage(page);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveFilters, businessId, target, enabled],
  );

  // Re-run the fetch for the page the admin is currently on. Used after a
  // promo is created/updated so the list refreshes in place instead of a full
  // page reload.
  const refetchCurrentPage = useCallback(async () => {
    await fetchAllTierList({page: currentPageRef.current});
  }, [fetchAllTierList]);

  // Without a businessId this searches the page on screen; the promo list
  // endpoint has no name filter.
  const handleAutoComplete = (searchTerm: string) => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      setItemTotal(serverTotal);
      setTierList(fullTierList.slice(0, 10));
      return;
    }
    const filtered = fullTierList.filter((item) =>
      item.name?.toLowerCase().includes(trimmed),
    );
    setItemTotal(filtered.length);
    setTierList(filtered.slice(0, 10));
  };

  useEffect(() => {
    // `fetchAllTierList` try/catches into `setError`, so this only fires if
    // that catch block itself throws. Routed to the same state the fetcher
    // uses, so the screen's existing error surface still reflects it.
    fetchAllTierList({}).catch((e: unknown) => setError(e));
  }, [fetchAllTierList]);

  const handleApplyFilters = useCallback(() => {
    // Layer the URL `status` onto a copy instead of mutating the state object.
    // `applyFilters` receives exactly the same filters as before; the
    // difference is that `filtersToApply` state is no longer written behind
    // React's back, so it stays owned by `selectAppliedFilters`.
    applyFilters(status ? {...filtersToApply, status} : filtersToApply);
    setIsPageReset((prev) => !prev); // Reset pagination to the first page
  }, [applyFilters, filtersToApply, status]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | number | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
    if (status) {
      setCurrentFilters({status});
    }
  };

  return {
    error,
    isLoading,
    pageInfo: {
      total: itemTotal,
      page,
    },
    list: tierList,
    fetchList: fetchAllTierList,
    refetchCurrentPage,
    displayPage,
    setPage,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
    handleAutoComplete,
  };
};

export default useTierList;
